import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            Last Updated: October 14, 2025
          </p>
        </div>

        {/* Content */}
        <div className="bg-white shadow-md rounded-lg p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Introduction
            </h2>
            <p className="text-gray-600 leading-relaxed">
              242 Network ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you use our mobile application and web services 
              (collectively, the "Services").
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Information We Collect
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Personal Information
                </h3>
                <p className="text-gray-600 mb-2">
                  We collect information that you provide directly to us, including:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                  <li>Name, email address, and phone number</li>
                  <li>Account credentials (username and password)</li>
                  <li>Profile information and biographical details</li>
                  <li>Contact history and communication records</li>
                  <li>Application and assessment information</li>
                  <li>Training and educational records</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Automatically Collected Information
                </h3>
                <p className="text-gray-600 mb-2">
                  When you use our Services, we may automatically collect:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                  <li>Device information (device type, operating system, unique device identifiers)</li>
                  <li>Log information (IP address, access times, app features used)</li>
                  <li>Usage data and analytics</li>
                  <li>Location information (with your permission)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              How We Use Your Information
            </h2>
            <p className="text-gray-600 mb-3">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Provide, maintain, and improve our Services</li>
              <li>Process your application and manage your onboarding</li>
              <li>Communicate with you about your account and provide support</li>
              <li>Send you notifications, updates, and administrative messages</li>
              <li>Track training progress and assessment results</li>
              <li>Facilitate connections between church planters and hub leaders</li>
              <li>Analyze usage patterns and improve user experience</li>
              <li>Comply with legal obligations and protect our rights</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Information Sharing and Disclosure
            </h2>
            <p className="text-gray-600 mb-3">
              We may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>
                <strong>With Other Users:</strong> Hub leaders, coaches, and staff members 
                can view information relevant to their role in supporting church planters
              </li>
              <li>
                <strong>With Service Providers:</strong> Third-party vendors who assist 
                with hosting, analytics, email delivery, and other services
              </li>
              <li>
                <strong>With Partner Organizations:</strong> MS Baptist Convention and 
                other ministry partners as necessary for program administration
              </li>
              <li>
                <strong>For Legal Purposes:</strong> When required by law or to protect 
                our rights and safety
              </li>
              <li>
                <strong>With Your Consent:</strong> When you explicitly authorize us to 
                share information
              </li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Data Security
            </h2>
            <p className="text-gray-600 leading-relaxed">
              We implement appropriate technical and organizational measures to protect 
              your personal information against unauthorized access, alteration, disclosure, 
              or destruction. These measures include encryption, secure servers, access controls, 
              and regular security assessments. However, no method of transmission over the 
              Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Data Retention
            </h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the 
              purposes outlined in this Privacy Policy, unless a longer retention period 
              is required or permitted by law. When we no longer need your information, 
              we will securely delete or anonymize it.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Your Rights and Choices
            </h2>
            <p className="text-gray-600 mb-3">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>
                <strong>Access and Update:</strong> You can access and update your account 
                information through the app or web portal
              </li>
              <li>
                <strong>Delete:</strong> You can request deletion of your account by 
                contacting us at{' '}
                <a 
                  href="mailto:privacy@242network.com"
                  className="text-primary-600 hover:text-primary-700"
                >
                  privacy@242network.com
                </a>
              </li>
              <li>
                <strong>Opt-Out:</strong> You can opt out of promotional emails by following 
                the unsubscribe instructions in those emails
              </li>
              <li>
                <strong>Data Portability:</strong> You can request a copy of your data in 
                a portable format
              </li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Children's Privacy
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Our Services are not intended for individuals under the age of 18. We do not 
              knowingly collect personal information from children. If you believe we have 
              collected information from a child, please contact us immediately.
            </p>
          </section>

          {/* Push Notifications */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Push Notifications
            </h2>
            <p className="text-gray-600 leading-relaxed">
              With your permission, we may send push notifications to your mobile device 
              to provide updates, reminders, and important information. You can disable 
              push notifications at any time through your device settings or the app settings.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Changes to This Privacy Policy
            </h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of 
              any changes by posting the new Privacy Policy on this page and updating the 
              "Last Updated" date. You are advised to review this Privacy Policy periodically 
              for any changes.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Contact Us
            </h2>
            <p className="text-gray-600 mb-3">
              If you have any questions about this Privacy Policy or our privacy practices, 
              please contact us at:
            </p>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-gray-900 font-medium">242 Network</p>
              <p className="text-gray-600">
                Email:{' '}
                <a 
                  href="mailto:privacy@242network.com"
                  className="text-primary-600 hover:text-primary-700"
                >
                  privacy@242network.com
                </a>
              </p>
              <p className="text-gray-600">
                Support:{' '}
                <a 
                  href="mailto:support@242network.com"
                  className="text-primary-600 hover:text-primary-700"
                >
                  support@242network.com
                </a>
              </p>
            </div>
          </section>

          {/* Consent */}
          <section className="bg-gray-50 p-6 rounded-md">
            <p className="text-gray-700 leading-relaxed">
              By using our Services, you acknowledge that you have read and understood this 
              Privacy Policy and agree to its terms.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;



